import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import React, { MouseEvent, ReactNode, useState } from 'react';
import { EntitySecurityProvider, SECURITY_ACTIONS, SecuredIconButton, SecuredMenuItem } from '../auth/Security';
import Confirm from '../components/Confirm';
import SavePreferenceModal from '../components/SavePreferenceModal';
import { BUTTON_VARIANTS, SIZES } from '../constants/constants';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import type { SavePreferenceFormData } from '../types/savePreference';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import type { ColumnViewSelection, SavedColumnView } from './columnViewTypes';
import { PRESET_INDEX } from './columnVisibilitySelectorConstants';

/** Fail closed when preference security is missing (shared views without Edit/Delete). */
const EMPTY_PREFERENCE_SECURITY = { allowedActions: [] as string[] };

type SavedColumnViewsSharedProps = {
    savedColumnViews: SavedColumnView[];
    selectedSavedViewId: string | null;
    selectedPreset: number;
    hasUnsavedColumnViewChanges: boolean;
    savingColumnView: boolean;
    deletingColumnView: boolean;
    onSaveColumnView: (formData: SavePreferenceFormData) => Promise<SavedColumnView | null>;
    onUpdateColumnView?: () => Promise<SavedColumnView | null>;
    onDeleteColumnView?: (viewId: string) => Promise<boolean>;
    onPresetSelected: (index: number, selection: ColumnViewSelection) => void;
    onFavouriteViewDeleted?: (deletedView: SavedColumnView) => void | Promise<void>;
};

type SavedColumnViewsMenuItemsProps = Pick<
    SavedColumnViewsSharedProps,
    | 'savedColumnViews'
    | 'selectedSavedViewId'
    | 'selectedPreset'
    | 'deletingColumnView'
    | 'onDeleteColumnView'
    | 'onPresetSelected'
> & {
    onCloseMenu: () => void;
    onRequestDelete: (view: SavedColumnView) => void;
    renderFavouriteStar: (
        label: string,
        model: GridColumnVisibilityModel,
        index: number,
        orderedFields?: string[],
        savedViewId?: string,
        gridSettings?: SavedColumnView['gridSettings']
    ) => ReactNode;
};

/** Menu entries for saved views — render inside the presets `<Menu>` only. */
export function SavedColumnViewsMenuItems({
    savedColumnViews,
    selectedSavedViewId,
    selectedPreset,
    deletingColumnView,
    onDeleteColumnView,
    onPresetSelected,
    onCloseMenu,
    onRequestDelete,
    renderFavouriteStar
}: SavedColumnViewsMenuItemsProps) {
    const t = useI18n();

    if (savedColumnViews.length === 0) {
        return null;
    }

    return (
        <>
            <Divider />
            {savedColumnViews.map((view) => (
                <MenuItem
                    key={view.id}
                    selected={selectedPreset === PRESET_INDEX.saved && selectedSavedViewId === view.id}
                    onClick={() => {
                        onPresetSelected(PRESET_INDEX.saved, {
                            visibilityModel: view.columnVisibilityModel,
                            orderedFields: view.orderedFields,
                            savedViewId: view.id,
                            gridSettings: view.gridSettings
                        });
                        onCloseMenu();
                    }}
                    sx={{
                        '& .column-view-delete-btn': { opacity: 0 },
                        '&:hover .column-view-delete-btn': { opacity: 1 }
                    }}
                >
                    <ListItemIcon>
                        <Icons.ListViewTable />
                    </ListItemIcon>
                    <ListItemText>{view.name}</ListItemText>
                    {onDeleteColumnView && (
                        <EntitySecurityProvider
                            entityName={ENTITY_CONSTANTS.preference}
                            entitySecurity={view.security ?? EMPTY_PREFERENCE_SECURITY}
                        >
                            <SecuredIconButton
                                className="column-view-delete-btn"
                                securityAction={SECURITY_ACTIONS.DELETE}
                                size="small"
                                disabled={deletingColumnView}
                                aria-label={t('actions.delete')}
                                title={t('actions.delete')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRequestDelete(view);
                                }}
                            >
                                <Icons.Delete color="disabled" />
                            </SecuredIconButton>
                        </EntitySecurityProvider>
                    )}
                    {renderFavouriteStar(
                        view.name,
                        view.columnVisibilityModel,
                        PRESET_INDEX.saved,
                        view.orderedFields,
                        view.id,
                        view.gridSettings
                    )}
                </MenuItem>
            ))}
        </>
    );
}

type SavedColumnViewsChromeProps = SavedColumnViewsSharedProps & {
    viewPendingDelete: SavedColumnView | null;
    onViewPendingDeleteChange: (view: SavedColumnView | null) => void;
};

/** Save button, save modal, and delete confirm — mount only when `enableSaveColumnView`. */
export function SavedColumnViewsChrome({
    savedColumnViews,
    selectedSavedViewId,
    hasUnsavedColumnViewChanges,
    savingColumnView,
    deletingColumnView,
    onSaveColumnView,
    onUpdateColumnView,
    onDeleteColumnView,
    onPresetSelected,
    onFavouriteViewDeleted,
    viewPendingDelete,
    onViewPendingDeleteChange
}: SavedColumnViewsChromeProps) {
    const t = useI18n();
    const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [saveViewOpen, setSaveViewOpen] = useState(false);

    const selectedSavedView = savedColumnViews.find((view) => view.id === selectedSavedViewId) ?? null;

    async function handleSaveView(formData: SavePreferenceFormData & { data: unknown }) {
        const saved = await onSaveColumnView(formData);
        if (!saved) {
            // Keep SavePreferenceModal open; it toasts config.errorMessage on throw.
            throw new Error('Failed to save column view');
        }
        successToastMessage('grid.preference.saved.text');
        onPresetSelected(PRESET_INDEX.saved, {
            visibilityModel: saved.columnVisibilityModel,
            orderedFields: saved.orderedFields,
            savedViewId: saved.id,
            gridSettings: saved.gridSettings
        });
    }

    function handleSaveButtonClick(event: MouseEvent<HTMLElement>) {
        if (selectedSavedView && onUpdateColumnView) {
            setSaveMenuAnchorEl(event.currentTarget);
            return;
        }
        setSaveViewOpen(true);
    }

    async function handleSaveExisting() {
        setSaveMenuAnchorEl(null);
        if (!onUpdateColumnView) {
            return;
        }
        const saved = await onUpdateColumnView();
        if (!saved) {
            failedToastMessage('grid.preference.failedToSave.text');
            return;
        }
        successToastMessage('grid.preference.saved.text');
        onPresetSelected(PRESET_INDEX.saved, {
            visibilityModel: saved.columnVisibilityModel,
            orderedFields: saved.orderedFields,
            savedViewId: saved.id,
            gridSettings: saved.gridSettings
        });
    }

    async function handleConfirmDelete() {
        if (!viewPendingDelete || !onDeleteColumnView) {
            return;
        }
        const deletedView = viewPendingDelete;
        const deletedId = deletedView.id;
        const deleted = await onDeleteColumnView(deletedId);
        onViewPendingDeleteChange(null);
        if (!deleted) {
            return;
        }
        // Clear the favourite before opening All Columns. Doing it after rebuilds the
        // restored groups collapsed, the same way an ungrouped view used to.
        await onFavouriteViewDeleted?.(deletedView);
        if (selectedSavedViewId === deletedId) {
            onPresetSelected(PRESET_INDEX.all, { visibilityModel: {} });
        }
    }

    return (
        <>
            {hasUnsavedColumnViewChanges && (
                <>
                    <Button
                        variant={BUTTON_VARIANTS.outlined}
                        size={SIZES.small}
                        color="primary"
                        startIcon={<Icons.Save />}
                        disabled={savingColumnView}
                        onClick={handleSaveButtonClick}
                    >
                        {t('grid.column.views.save')}
                    </Button>
                    {selectedSavedView && onUpdateColumnView && (
                        <EntitySecurityProvider
                            entityName={ENTITY_CONSTANTS.preference}
                            entitySecurity={selectedSavedView.security ?? EMPTY_PREFERENCE_SECURITY}
                        >
                            <Menu
                                anchorEl={saveMenuAnchorEl}
                                open={Boolean(saveMenuAnchorEl)}
                                onClose={() => setSaveMenuAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                <SecuredMenuItem securityAction={SECURITY_ACTIONS.EDIT} onClick={handleSaveExisting}>
                                    {t('common.save')}
                                </SecuredMenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setSaveMenuAnchorEl(null);
                                        setSaveViewOpen(true);
                                    }}
                                >
                                    {t('common.save.as')}
                                </MenuItem>
                            </Menu>
                        </EntitySecurityProvider>
                    )}
                </>
            )}

            <SavePreferenceModal
                open={saveViewOpen}
                onClose={() => setSaveViewOpen(false)}
                onSave={handleSaveView}
                data={null}
                isLoading={savingColumnView}
                config={{
                    modalId: 'save-column-view-modal',
                    title: 'grid.column.views.saveAs.title',
                    namePlaceholder: 'grid.column.views.saveAs.namePlaceholder',
                    descriptionPlaceholder: 'grid.column.views.saveAs.descriptionPlaceholder',
                    errorMessage: 'grid.preference.failedToSave.text',
                    hidePinAsTab: true
                }}
            />

            <Confirm
                open={Boolean(viewPendingDelete)}
                title={t('delete.confirm.title')}
                body={t('grid.column.views.delete.confirm', { name: viewPendingDelete?.name ?? '' })}
                okLabelI18n="dialog.delete"
                onConfirm={handleConfirmDelete}
                onCancel={() => onViewPendingDeleteChange(null)}
                isLoading={deletingColumnView}
                isOkButtonContained
            />
        </>
    );
}
